import { action, computed, makeObservable, observable } from 'mobx'
import { ContainerPositionSnapshot, PositionDto } from '@yard/app/api'

// Position recorded at the moment the planner accumulated a container into a
// housekeeping group. Used by L2.3 race-drop detection: if another operator
// moves the container before the planner clicks Confirm, the BE compares this
// snapshot to the container's current occupied position, drops it from the
// plan, and surfaces it as an inline warning.
//
// containerNumber is kept here so the drop warning can show the ISO box code
// (e.g. "BMOU1234567") instead of the internal container id — planners
// recognise the ISO number, not the database key. The number is captured at
// accumulation time because a race-dropped container may have moved off the
// currently-loaded blocks by the time the warning renders.
interface AccumulatedSnapshot {
  blockId: string
  position: PositionDto
  containerNumber?: string
}

export class HousekeepingPlanAccumulatorViewStore {
  isAccumulatorOpen = false
  isPlanDialogOpen = false
  accumulated: Set<number> = new Set()
  checked: Set<number> = new Set()
  // Keyed on containerId — kept in step with `accumulated`. Map (not a plain
  // object) so MobX can react to add/delete operations atomically.
  snapshots: Map<number, AccumulatedSnapshot> = new Map()

  constructor() {
    makeObservable(this, {
      isAccumulatorOpen: observable,
      isPlanDialogOpen: observable,
      accumulated: observable,
      checked: observable,
      snapshots: observable,
      accumulatedCount: computed,
      checkedCount: computed,
      hasAnyChecked: computed,
      allChecked: computed,
      activate: action,
      deactivate: action,
      add: action,
      remove: action,
      clearAccumulator: action,
      toggleChecked: action,
      selectAllChecked: action,
      deselectAllChecked: action,
      openPlanDialog: action,
      closePlanDialog: action,
    })
  }

  get accumulatedCount(): number {
    return this.accumulated.size
  }

  get checkedCount(): number {
    return this.checked.size
  }

  get hasAnyChecked(): boolean {
    return this.checked.size > 0
  }

  get allChecked(): boolean {
    return this.accumulatedCount > 0 && this.checkedCount === this.accumulatedCount
  }

  isAccumulated(containerId: number): boolean {
    return this.accumulated.has(containerId)
  }

  isChecked(containerId: number): boolean {
    return this.checked.has(containerId)
  }

  activate() {
    this.isAccumulatorOpen = true
  }

  deactivate() {
    this.isAccumulatorOpen = false
    this.isPlanDialogOpen = false
    this.accumulated.clear()
    this.checked.clear()
    this.snapshots.clear()
  }

  add(containerId: number, snapshot: AccumulatedSnapshot) {
    if (!this.isAccumulatorOpen) return
    this.accumulated.add(containerId)
    this.checked.add(containerId)
    // Last-write-wins: a planner clicking the same container twice in a row
    // (with a stale tile in between) gets the freshest snapshot.
    this.snapshots.set(containerId, snapshot)
  }

  remove(containerId: number) {
    if (!this.isAccumulatorOpen) return
    this.accumulated.delete(containerId)
    this.checked.delete(containerId)
    this.snapshots.delete(containerId)
  }

  clearAccumulator() {
    if (!this.isAccumulatorOpen) return
    this.accumulated.clear()
    this.checked.clear()
    this.snapshots.clear()
  }

  toggleChecked(containerId: number) {
    if (!this.isAccumulatorOpen) return
    if (!this.accumulated.has(containerId)) return
    if (this.checked.has(containerId)) {
      this.checked.delete(containerId)
    } else {
      this.checked.add(containerId)
    }
  }

  selectAllChecked() {
    if (!this.isAccumulatorOpen) return
    this.accumulated.forEach(id => this.checked.add(id))
  }

  deselectAllChecked() {
    if (!this.isAccumulatorOpen) return
    this.checked.clear()
  }

  openPlanDialog() {
    if (!this.isAccumulatorOpen) return
    if (!this.hasAnyChecked) return
    this.isPlanDialogOpen = true
  }

  closePlanDialog() {
    this.isPlanDialogOpen = false
  }

  /**
   * Look up the ISO container number captured when the planner accumulated
   * this container. Used to render the L2.3 race-drop warning by box code
   * instead of by internal id. Falls back to undefined when the snapshot
   * exists but the number was missing at add-time, or the id was never
   * accumulated (e.g. an unrelated id slipped into the dropped list).
   */
  containerNumberFor(containerId: number): string | undefined {
    return this.snapshots.get(containerId)?.containerNumber
  }

  /**
   * Snapshot payload to send with CreateHousekeepingPlanCommand for every
   * currently-checked container. Containers without a recorded snapshot are
   * skipped — `add` always records one, so this only matters defensively.
   */
  snapshotsForChecked(): ContainerPositionSnapshot[] {
    return [...this.checked]
      .map((containerId): ContainerPositionSnapshot | undefined => {
        const snapshot = this.snapshots.get(containerId)
        if (!snapshot) return undefined
        return {
          containerId,
          blockId: snapshot.blockId,
          position: snapshot.position,
        }
      })
      .filter((s): s is ContainerPositionSnapshot => s !== undefined)
  }
}
